import { Check } from 'lucide-react';

import type { PricedItem } from '@/app/types/item';
import { formatInr, formatQuantity, priceLadder, resolveRate } from '@/app/lib/pricing';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { cn } from '@/lib/utils';

interface Props {
    item: PricedItem | null;
    quantity: number;
    onClose: () => void;
}

/**
 * The full bulk-price ladder for one item.
 *
 * Product rows only have room for the next couple of price breaks, and a fifth
 * of the priced catalogue carries three to eight. This is where a manager sees
 * the whole ladder before quoting.
 */
export function TierSheet({ item, quantity, onClose }: Props) {
    const ladder = item ? priceLadder(item, quantity) : [];
    const baseRate = item ? resolveRate(item, 0).rate : 0;
    const current = item ? resolveRate(item, quantity > 0 ? quantity : 1).rate : 0;

    // The step in force right now — the last one whose threshold is met.
    const activeMinQty = ladder.filter((s) => s.reached).at(-1)?.minQty ?? null;

    // The quoted ladder starts at qty 0, so any first step above 0 leaves a real
    // band before it (spices sold by the gram: "Under 0.2 kg"). Show it.
    const showBase = baseRate > 0 && (ladder[0]?.minQty ?? 0) > 0;

    return (
        <MobileBottomSheet
            open={!!item}
            onClose={onClose}
            title="Bulk pricing"
            heightMode="auto"
        >
            {item && (
                <div className="p-4 space-y-4">
                    <div>
                        <p className="font-semibold text-foreground leading-snug">
                            {item.name}
                        </p>
                        <p className="text-sm text-muted-foreground mt-0.5 tabular-nums">
                            {quantity > 0
                                ? `${formatQuantity(quantity)} ${item.unitName} at ${formatInr(current)}`
                                : `${formatInr(current)} / ${item.unitName}`}
                        </p>
                    </div>

                    <div className="rounded-xl border border-border overflow-hidden divide-y divide-border">
                        {showBase && (
                            <LadderRow
                                qtyLabel={`Under ${formatQuantity(ladder[0].minQty)}`}
                                unitName={item.unitName}
                                rate={baseRate}
                                active={activeMinQty === null}
                                reached
                            />
                        )}
                        {ladder.map((step) => (
                            <LadderRow
                                key={step.minQty}
                                qtyLabel={`${formatQuantity(step.minQty)}+`}
                                unitName={item.unitName}
                                rate={step.rate}
                                label={step.label}
                                active={activeMinQty === step.minQty}
                                reached={step.reached}
                            />
                        ))}
                    </div>

                    <p className="text-xs text-muted-foreground">
                        Rates apply automatically once the quantity reaches the
                        threshold. They cannot be edited here.
                    </p>
                </div>
            )}
        </MobileBottomSheet>
    );
}

interface LadderRowProps {
    qtyLabel: string;
    unitName: string;
    rate: number;
    label?: string | null;
    active: boolean;
    reached: boolean;
}

function LadderRow({ qtyLabel, unitName, rate, label, active, reached }: LadderRowProps) {
    return (
        <div
            className={cn(
                'flex items-center justify-between gap-3 px-4 py-3',
                active && 'bg-primary/10'
            )}
        >
            <div className="min-w-0">
                <p
                    className={cn(
                        'text-sm tabular-nums',
                        reached ? 'text-foreground' : 'text-muted-foreground'
                    )}
                >
                    {qtyLabel} {unitName}
                </p>
                {label && (
                    <p className="text-[11px] text-muted-foreground truncate">{label}</p>
                )}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
                <span
                    className={cn(
                        'tabular-nums',
                        active
                            ? 'text-base font-semibold text-primary'
                            : 'text-sm font-medium text-foreground'
                    )}
                >
                    {formatInr(rate)}
                </span>
                {active && <Check className="h-4 w-4 text-primary" />}
            </div>
        </div>
    );
}
